import React, { useState, useEffect } from 'react';
import { Database, FolderArchive, FileText, Download, AlertCircle, CheckCircle, Clock, HardDrive, Package } from 'lucide-react';
import backupService, { BackupInfo, MediaBackupStatus } from '../../services/backup';

const BackupSection: React.FC = () => {
    const [backupInfo, setBackupInfo] = useState<BackupInfo | null>(null);
    const [mediaStatus, setMediaStatus] = useState<MediaBackupStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingMediaStatus, setLoadingMediaStatus] = useState(false);
    const [downloadingDatabase, setDownloadingDatabase] = useState(false);
    const [downloadingMedia, setDownloadingMedia] = useState(false);
    const [downloadingLogs, setDownloadingLogs] = useState(false);
    const [logsDays, setLogsDays] = useState<7 | 14 | 30>(7);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Load backup info on mount
    useEffect(() => {
        loadBackupInfo();
        loadMediaStatus();
    }, []);

    const loadBackupInfo = async () => {
        try {
            setLoading(true);
            const info = await backupService.getBackupInfo();
            setBackupInfo(info);
        } catch (err: any) {
            console.error('Failed to load backup info:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadMediaStatus = async () => {
        try {
            setLoadingMediaStatus(true);
            const status = await backupService.getMediaBackupStatus();
            setMediaStatus(status);
        } catch (err: any) {
            console.error('Failed to load media backup status:', err);
        } finally {
            setLoadingMediaStatus(false);
        }
    };

    const handleDatabaseBackup = async () => {
        try {
            setDownloadingDatabase(true);
            setError(null);
            setSuccess(null);

            await backupService.downloadDatabaseBackup();

            setSuccess('Database backup downloaded successfully!');
            setTimeout(() => setSuccess(null), 5000);
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || 'Failed to download database backup';
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
        } finally {
            setDownloadingDatabase(false);
        }
    };

    const handleMediaBackup = async () => {
        try {
            setDownloadingMedia(true);
            setError(null);
            setSuccess(null);

            await backupService.downloadMediaBackup();

            setSuccess('Media files backup downloaded successfully!');
            setTimeout(() => setSuccess(null), 5000);

            // Reload media status after download
            await loadMediaStatus();
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || 'Failed to download media backup';
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
        } finally {
            setDownloadingMedia(false);
        }
    };

    const handleLogsBackup = async () => {
        try {
            setDownloadingLogs(true);
            setError(null);
            setSuccess(null);

            await backupService.downloadLogsBackup(logsDays);

            setSuccess(`System logs (${logsDays} days) downloaded successfully!`);
            setTimeout(() => setSuccess(null), 5000);
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || 'Failed to download logs backup';
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
        } finally {
            setDownloadingLogs(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900">System Backups</h2>
                <p className="text-sm text-gray-600 mt-1">
                    Download backups manually. You are responsible for storing backups securely.
                </p>
            </div>

            {/* Error/Success Messages */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-800">{error}</div>
                </div>
            )}

            {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-green-800">{success}</div>
                </div>
            )}

            {/* Backup Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Database Backup */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col">
                    <div className="flex items-start mb-4">
                        <div className="h-12 w-12 bg-blue-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <Database className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">Database Backup</h3>
                            <p className="text-xs text-gray-500">SQL dump file</p>
                        </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                        Download complete database as SQL file
                    </p>

                    {/* Database Info */}
                    <div className="space-y-3 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center text-xs text-gray-600 mb-1">
                                <Database className="h-3 w-3 mr-1" />
                                Backup Type
                            </div>
                            <div className="text-base font-semibold text-gray-900">
                                PostgreSQL Dump
                            </div>
                        </div>

                        {/* Database Size Info */}
                        {backupInfo && (
                            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center text-xs text-blue-700 font-medium">
                                        <HardDrive className="h-3 w-3 mr-1" />
                                        Database Size
                                    </div>
                                </div>
                                <div className="flex items-baseline justify-between">
                                    <div className="text-base font-semibold text-blue-900">
                                        {backupInfo.database.size_mb.toFixed(2)} MB
                                    </div>
                                    <div className="text-xs text-blue-600">
                                        Full backup
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex-1" />

                    <button
                        onClick={handleDatabaseBackup}
                        disabled={downloadingDatabase}
                        className="w-full flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {downloadingDatabase ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                Downloading...
                            </>
                        ) : (
                            <>
                                <Download className="h-4 w-4 mr-2" />
                                Download Database
                            </>
                        )}
                    </button>
                </div>

                {/* Media Files Backup */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col">
                    <div className="flex items-start mb-4">
                        <div className="h-12 w-12 bg-green-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <FolderArchive className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="font-semibold text-gray-900">Media Files</h3>
                                    <p className="text-xs text-gray-500">Compressed archive</p>
                                </div>
                                {mediaStatus?.latest_backup.is_current && (
                                    <div className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full border border-green-200">
                                        <CheckCircle className="h-3 w-3 mr-1" />
                                        Current
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                        All uploaded files (images, documents, attachments)
                    </p>

                    <div className="flex-1" />

                    {loadingMediaStatus ? (
                        <div className="space-y-3 mb-4">
                            <div className="bg-gray-50 rounded-lg p-3 animate-pulse">
                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                            </div>
                        </div>
                    ) : mediaStatus ? (
                        <div className="space-y-3 mb-4">
                            {/* Current Media */}
                            <div className="bg-gray-50 rounded-lg p-3">
                                <div className="flex items-center text-xs text-gray-600 mb-1">
                                    <HardDrive className="h-3 w-3 mr-1" />
                                    Current Media
                                </div>
                                <div className="flex items-baseline justify-between">
                                    <div className="text-base font-semibold text-gray-900">
                                        {mediaStatus.current_media.total_size_mb.toFixed(2)} MB
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        {mediaStatus.current_media.file_count} files
                                    </div>
                                </div>
                            </div>

                            {/* Last Backup */}
                            {mediaStatus.latest_backup.has_backup && (
                                <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center text-xs text-blue-700 font-medium">
                                            <Package className="h-3 w-3 mr-1" />
                                            Last Backup
                                        </div>
                                        <div className="flex items-center text-xs text-blue-600">
                                            <Clock className="h-3 w-3 mr-1" />
                                            {new Date(mediaStatus.latest_backup.backup_created_at!).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <div className="text-xs text-blue-600">Backup Size</div>
                                            <div className="text-sm font-semibold text-blue-900">
                                                {mediaStatus.latest_backup.backup_size_mb?.toFixed(2)} MB
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-blue-600">Compression</div>
                                            <div className="text-sm font-semibold text-blue-900">
                                                {((1 - (mediaStatus.latest_backup.compression_ratio || 0)) * 100).toFixed(0)}%
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : null}

                    <button
                        onClick={handleMediaBackup}
                        disabled={downloadingMedia}
                        className="w-full flex items-center justify-center px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {downloadingMedia ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                {mediaStatus?.latest_backup.is_current ? 'Downloading...' : 'Preparing backup...'}
                            </>
                        ) : (
                            <>
                                <Download className="h-4 w-4 mr-2" />
                                Download Media
                            </>
                        )}
                    </button>
                </div>

                {/* System Logs Backup */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col">
                    <div className="flex items-start mb-4">
                        <div className="h-12 w-12 bg-purple-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                            <FileText className="h-6 w-6 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">System Logs</h3>
                            <p className="text-xs text-gray-500">ZIP archive</p>
                        </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4">
                        Application logs for troubleshooting
                    </p>

                    {/* Logs Info */}
                    <div className="space-y-3 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-center text-xs text-gray-600 mb-1">
                                <FileText className="h-3 w-3 mr-1" />
                                Log Files
                            </div>
                            <div className="flex items-baseline justify-between">
                                <div className="text-base font-semibold text-gray-900">
                                    {backupInfo ? backupInfo.logs[`${logsDays}_days` as '7_days' | '14_days' | '30_days'] : '...'} files
                                </div>
                                <div className="text-xs text-gray-600">
                                    Last {logsDays} days
                                </div>
                            </div>
                        </div>

                        {/* Time Period Selector */}
                        <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                            <label htmlFor="logs-days" className="flex items-center text-xs text-purple-700 font-medium mb-2">
                                <Clock className="h-3 w-3 mr-1" />
                                Time Period
                            </label>
                            <select
                                id="logs-days"
                                value={logsDays}
                                onChange={(e) => setLogsDays(Number(e.target.value) as 7 | 14 | 30)}
                                className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                            >
                                <option value={7}>Last 7 days</option>
                                <option value={14}>Last 14 days</option>
                                <option value={30}>Last 30 days</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex-1" />

                    <button
                        onClick={handleLogsBackup}
                        disabled={downloadingLogs}
                        className="w-full flex items-center justify-center px-4 py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {downloadingLogs ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                Downloading...
                            </>
                        ) : (
                            <>
                                <Download className="h-4 w-4 mr-2" />
                                Download Logs
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BackupSection;
